import ExportFiscalMensuel from '../components/shared/ExportFiscalMensuel';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Wallet, BookOpen } from '../lib/icons';
import { toast } from 'sonner';
import {
  fetchPersonnel,
  fetchPointages,
  validerJournee,
  fetchCompteTiers,
  nomTiers,
  salaireDefaut,
  TYPES_TRAVAIL,
} from '../lib/dataProvider';
import { fmt, PageTitle, StatBlock } from '../components/shared/badges';
import { PaiementTiersDialog } from '../components/reception/PaiementTiersDialog';
import { GrandLivreTiers } from '../components/releves/GrandLivreTiers';
import { ActionsAdmin } from '../components/shared/ActionsAdmin';
import { useAuth } from '../lib/auth';
import { estAdmin, peut } from '../lib/permissions';
import { adminModifierPointage, adminSupprimerPointage } from '../lib/dataProvider';

const inputCls =
  'h-10 w-full border border-slate-300 bg-slate-50 px-2 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';

const ligneInit = (p) => ({
  tiersId: p.id,
  present: false,
  typeTravail: p.contrat === 'Mensuel' ? 'Mensuel' : TYPES_TRAVAIL[0],
  montant: '',
  notes: '',
});

export default function PointagePaie() {
  const [personnel, setPersonnel] = useState([]);
  const [pointages, setPointages] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [paiement, setPaiement] = useState(null);
  const [livre, setLivre] = useState(null);
  const { user } = useAuth();
  const voitSoldes = peut(user?.role, 'pointage-paie', 'soldes');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [grille, setGrille] = useState({});

  const load = useCallback(async () => {
    const gens = await fetchPersonnel();
    setPersonnel(gens);
    setPointages(await fetchPointages());
    setComptes(await Promise.all(gens.map((g) => fetchCompteTiers(g.id))));
    setGrille(Object.fromEntries(gens.map((g) => [g.id, ligneInit(g)])));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dejaPointes = useMemo(
    () => new Set(pointages.filter((p) => p.date === date).map((p) => p.tiersId)),
    [pointages, date]
  );

  const maj = (id, patch) => setGrille((g) => ({ ...g, [id]: { ...g[id], ...patch } }));

  const cocher = (emp, present) => {
    const l = grille[emp.id];
    maj(emp.id, {
      present,
      montant: present ? String(salaireDefaut(emp, l.typeTravail)) : '',
    });
  };

  const changerType = (emp, typeTravail) => {
    const l = grille[emp.id];
    maj(emp.id, { typeTravail, montant: l.present ? String(salaireDefaut(emp, typeTravail)) : l.montant });
  };

  const valider = async () => {
    const lignes = personnel.filter((p) => !dejaPointes.has(p.id)).map((p) => grille[p.id]);
    const { error, rows, total } = await validerJournee({ date, lignes });
    if (error) return toast.error(error);
    toast.success(`Journée du ${date} validée — ${rows.length} employé(s), ${fmt(total, 3)} TND crédités`);
    load();
  };

  const totalDu = comptes.reduce((s, c) => s + Math.max(0, c.solde), 0);
  const presents = personnel.filter((p) => grille[p.id]?.present && !dejaPointes.has(p.id));
  const totalGrille = presents.reduce((s, p) => s + (Number(grille[p.id].montant) || 0), 0);

  return (
    <div data-testid="pointage-page" className="space-y-6">
      <PageTitle
        emoji="👷"
        titre="Pointage & Paie"
        sous="Grille de pointage du jour — saisie en masse et comptes courants du personnel"
      />

      <ExportFiscalMensuel type="cnss" />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <StatBlock testid="stat-personnel" titre="Personnel" accent="border-l-slate-900" items={[['Employés', fmt(personnel.length)]]} />
        <StatBlock
          testid="stat-jour"
          titre="Journée en cours"
          accent="border-l-sky-600"
          items={[['Présents', fmt(presents.length)], ['À créditer (TND)', fmt(totalGrille, 3)]]}
        />
        {voitSoldes && (
          <StatBlock testid="stat-salaires-dus" titre="Salaires dus" accent="border-l-orange-500" items={[['Total (TND)', fmt(totalDu, 3)]]} />
        )}
      </div>

      <section className="border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="font-heading text-base font-bold tracking-tight text-slate-900">Grille de pointage du jour</p>
            <p className="text-xs text-slate-500">
              Cochez les présents — le montant est prérempli d'après la fiche de l'employé et reste modifiable.
            </p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Date du pointage</label>
            <input
              data-testid="pointage-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={`mt-1.5 ${inputCls} sm:w-44`}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="grille-pointage">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Présent', 'Employé', 'Contrat', 'Salaire jour / mois', 'Type', 'Montant à créditer (TND)', 'Note'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {personnel.length === 0 ? (
                <tr data-testid="personnel-empty">
                  <td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-500">
                    Aucun employé : créez un contact de type « Personnel » depuis la page Contacts.
                  </td>
                </tr>
              ) : (
                personnel.map((p) => {
                  const l = grille[p.id];
                  if (!l) return null;
                  const deja = dejaPointes.has(p.id);
                  return (
                    <tr key={p.id} data-testid={`grille-row-${p.id}`} className={`border-b border-slate-100 ${deja ? 'bg-slate-50' : 'hover:bg-slate-50'}`}>
                      <td className="px-3 py-2">
                        {deja ? (
                          <span data-testid={`deja-pointe-${p.id}`} className="font-data text-[10px] uppercase tracking-[0.08em] text-emerald-700">
                            déjà pointé
                          </span>
                        ) : (
                          <input
                            type="checkbox"
                            data-testid={`present-${p.id}`}
                            checked={l.present}
                            onChange={(e) => cocher(p, e.target.checked)}
                            className="h-5 w-5 accent-slate-900"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm font-semibold text-slate-900">{p.nom}</td>
                      <td className="px-3 py-2 text-sm text-slate-700">{p.contrat || '—'}</td>
                      <td className="tabular px-3 py-2 font-data text-xs text-slate-600">
                        {fmt(Number(p.salaireJour) || 0, 3)} / {fmt(Number(p.salaireMois) || 0, 3)}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          data-testid={`type-${p.id}`}
                          disabled={deja}
                          value={l.typeTravail}
                          onChange={(e) => changerType(p, e.target.value)}
                          className={`${inputCls} sm:w-40`}
                        >
                          {TYPES_TRAVAIL.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          data-testid={`montant-${p.id}`}
                          type="number"
                          step="0.001"
                          min="0"
                          disabled={deja || !l.present}
                          value={l.montant}
                          onChange={(e) => maj(p.id, { montant: e.target.value })}
                          className={`${inputCls} sm:w-36 disabled:bg-slate-100 disabled:text-slate-400`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          data-testid={`note-${p.id}`}
                          disabled={deja || !l.present}
                          value={l.notes}
                          onChange={(e) => maj(p.id, { notes: e.target.value })}
                          className={`${inputCls} disabled:bg-slate-100`}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-200 px-4 py-4">
          <button
            type="button"
            data-testid="valider-journee"
            onClick={valider}
            className="flex h-14 w-full items-center justify-center gap-2 bg-slate-900 px-6 font-heading text-base font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            <CheckCircle2 size={20} /> Valider la journée
            {presents.length > 0 && (
              <span className="font-data text-sm font-normal normal-case tracking-normal">
                — {presents.length} présent(s) · {fmt(totalGrille, 3)} TND
              </span>
            )}
          </button>
        </div>
      </section>

      <section className={`border border-slate-200 bg-white ${voitSoldes ? '' : 'hidden'}`}>
        <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold tracking-tight text-slate-900">
          Comptes courants du personnel
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-personnel">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Employé', 'Contrat', 'CIN', 'CNSS', 'Solde dû (TND)', 'Actions'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {personnel.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500">Aucun employé enregistré.</td>
                </tr>
              ) : (
                personnel.map((p) => {
                  const c = comptes.find((x) => x.tiersId === p.id);
                  return (
                    <tr key={p.id} data-testid={`personnel-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 text-sm font-semibold text-slate-900">{p.nom}</td>
                      <td className="px-3 py-2 text-sm text-slate-700">{p.contrat || '—'}</td>
                      <td className="px-3 py-2 font-data text-sm text-slate-700">{p.cin || '—'}</td>
                      <td className="px-3 py-2 font-data text-sm text-slate-700">{p.cnss || '—'}</td>
                      <td data-testid={`solde-personnel-${p.id}`} className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">
                        {fmt(Math.abs(c?.solde ?? 0), 3)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1.5">
                          <button
                            type="button"
                            data-testid={`verser-${p.id}`}
                            onClick={() => setPaiement(p.id)}
                            className="inline-flex items-center gap-1.5 border-2 border-slate-900 px-2 py-1 text-xs font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                          >
                            <Wallet size={13} /> Verser avance / salaire
                          </button>
                          <button
                            type="button"
                            data-testid={`extrait-personnel-${p.id}`}
                            onClick={() => setLivre(c)}
                            className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                          >
                            <BookOpen size={13} /> Extrait
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="border border-slate-200 bg-white">
        <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold tracking-tight text-slate-900">
          Historique des pointages
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-pointages">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Réf.', 'Date', 'Employé', 'Type de travail', 'Montant (TND)', 'Note', ''].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pointages.length === 0 ? (
                <tr data-testid="pointages-empty">
                  <td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500">Aucun pointage enregistré.</td>                </tr>
              ) : (
                pointages.map((p) => (
                  <tr key={p.id} data-testid={`pointage-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{p.id}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{p.date}</td>
                    <td className="px-3 py-2 text-sm text-slate-800">{nomTiers(p.tiersId)}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{p.typeTravail}</td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(p.montant, 3)}</td>
                    <td className="px-3 py-2 text-sm text-slate-600">{p.notes || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <ActionsAdmin
                        id={p.id}
                        row={p}
                        titre="Corriger le pointage"
                        champs={[
                          { key: 'date', label: 'Date', type: 'date' },
                          { key: 'typeTravail', label: 'Type de travail', options: TYPES_TRAVAIL },
                          { key: 'montant', label: 'Montant (TND)', type: 'number' },
                          { key: 'notes', label: 'Note', large: true },
                        ]}
                        onModifier={(patch) => adminModifierPointage(p.id, patch, user?.name)}
                        onSupprimer={() => adminSupprimerPointage(p.id, user?.name)}
                        onDone={load}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {paiement && <PaiementTiersDialog tiersId={paiement} onClose={() => setPaiement(null)} onDone={load} />}
      {livre && <GrandLivreTiers compte={livre} onClose={() => setLivre(null)} />}
    </div>
  );
}
